/**
 * **添加出版社** —— 「按书录入」里那个"建一册书"的浮窗（2026-10-18 定稿）。
 *
 * > 文件名与组件名沿用 `DictationNewTextbookForm`：它建的仍然是一条 `textbook`
 * > 记录（一册 = 出版社 + 年级 + 学期），但**界面上叫「添加出版社」**，
 * > 入口是「出版社」下拉的最后一项。
 *
 * ## 用户定稿（本轮，原话）
 *
 * > 不用，你就**只建出版社**就可以了。至于**年级和学期，它旁边不是有两个下拉菜单吗**？
 *
 * 所以这个浮窗里**只有一格**：
 *
 *   · **出版社**（必填）—— 界面收人话（`人民教育出版社`），
 *     由 `canonicalPublisherName()` 归一后**原样存进库**（见 `lib/dictationTextbook.ts`
 *     与 `@fht/shared` 的 `publisher.ts`；⛔ 不再换算成 `pub_` 代号）。
 *
 * ⛔ **没有年级 / 学期 / 课本名称这三格**：年级 / 学期用页面下那两格
 *    （它们**始终可选**，默认 小学一年级 / 上册；见
 *    `lib/dictationBooksPicker.ts` 的文件头）。建出来的那一册 =
 *    **这本出版社 + 页面上当前选中的年级 + 学期**。
 *
 * ## 提交之后发生什么（这个入口存在的全部意义）
 *
 *   · 建成功 → 自动**定位**到这一册（三格变成它的出版社 / 年级 / 学期）、刷新列表、
 *     给一句含「出版社 · 年级 · 学期」的成功提示，用户接着录三张表；
 *   · **重复**（同出版社 + 同年级 + 学期已有）→ 不报技术错误，直接把已有那册选上，
 *     并说"这一册的书已经有过了，已经帮你选上了"。
 *     这两条都在 `DictationWorkspace` 里（那里才有 mutation 与选中状态）。
 *
 * ## 关闭时**不保留**已填内容
 *
 * 关掉（`Escape` / 点遮罩 / ✕ / 「取消」）之后那一格清空：取消后立刻重开还留着
 * 上一次的字更糟（容易把两家不同的出版社建成同一家，而界面看不出异常）。
 * 实现上就是在 `open` 变成 true 时把内部状态重置成预填值（见下面的 effect）。
 *
 * ## 手机优先
 *
 *   · 弹窗内容 `max-h-[92vh] overflow-y-auto`（`Modal` 自带）—— 小屏也能滚；
 *   · 按钮 `size="lg"`（48px）+ 窄屏 `flex-col` ⇒ 两个按钮都占满一行；
 *   · `Escape` / 点遮罩 / 「取消」三条关法都在（遮罩关闭见 `closeOnBackdrop`）。
 */

import { useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { listPublisherCandidates, validatePublisherName } from '../../lib/dictationTextbook';
import type { TextbookView } from '../../lib/api/dictation';

/** 出版社候选（已有课本的名字 / 人话代号）的 `datalist` id。 */
const PUBLISHER_DATALIST_ID = 'dictation-add-publisher-candidates';

export interface DictationNewTextbookFormProps {
  /**
   * 弹窗是否打开。
   *
   * `false` 时**什么都不渲染**（连 DOM 都没有）—— 这正是"默认收起"。
   */
  open: boolean;
  /**
   * 已有课本（`null` = 没拿到）。
   *
   * 用处：**出版社候选**（`datalist`）—— 同一个出版社第二次录时是"选"，不是重打。
   */
  textbooks: TextbookView[] | null;
  /**
   * 打开时预填的出版社名（`''` = 从空白开始）。
   *
   * ⚠️ 现在只有「＋ 添加出版社」一个入口，所以**永远是空串**。
   *    这个 prop 留着是为了"打开时重置"这条语义（见下面的 effect）。
   */
  initialPublisher: string;
  /**
   * 会建在哪一册（页面下那两格当前的值，`小学一年级 · 上册`；空串 = 还没选好）。
   *
   * ⚠️ 只用于**说明**，不受这个弹窗控制：用户要换册就去关掉弹窗改那两格。
   */
  volumeLabel: string;
  submitting: boolean;
  /** 后端拒绝时的一句话（本地校验的话在表单里那一行，不走这里）。 */
  error: string | null;
  onCancel: () => void;
  /** 提交的是**出版社名**（人话）；年级 / 学期由页面当前的选择补上。 */
  onSubmit: (publisher: string) => void;
}

export function DictationNewTextbookForm({
  open,
  textbooks,
  initialPublisher,
  volumeLabel,
  submitting,
  error,
  onCancel,
  onSubmit,
}: DictationNewTextbookFormProps) {
  const [publisher, setPublisher] = useState('');
  /** 本地校验的那句话（点"添加出版社"之后才出现，见 `validatePublisherName`）。 */
  const [problem, setProblem] = useState<string | null>(null);

  /**
   * **每次打开都从 `initialPublisher` 开始**（关闭不保留已填内容，理由见文件头）。
   *
   * ⚠️ 依赖只有 `open` 与 `initialPublisher`（后者在弹窗打开期间不会变）：
   *    不能把 `textbooks` / `onCancel` 之类放进来 —— 那些在父组件每次渲染时
   *    身份都会变，一放进来就会在打字过程中把那一格清空。
   */
  useEffect(() => {
    if (!open) return;
    setPublisher(initialPublisher);
    setProblem(null);
  }, [open, initialPublisher]);

  const publisherCandidates = listPublisherCandidates(textbooks);

  const submit = () => {
    const localProblem = validatePublisherName(publisher);
    setProblem(localProblem);
    if (localProblem !== null) return;
    onSubmit(publisher);
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      title="添加出版社"
      description={
        volumeLabel === ''
          ? '一本课本 = 一册（出版社 + 年级 + 学期）。先在下面选好年级和学期，再回来添加出版社。'
          : `一本课本 = 一册：会建在「${volumeLabel}」这一册；要换年级 / 学期就去关掉这个窗口改下面那两格。`
      }
      onClose={onCancel}
      /**
       * ⚠️ 允许点遮罩关闭（`Modal` 默认**不**关，见那边的注释）。
       *    这一格是短字段，重新打的成本很小（而且关闭时本来就不保留）。
       */
      closeOnBackdrop
      size="md"
      footer={
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" size="lg" disabled={submitting} onClick={onCancel}>
            取消
          </Button>
          <Button size="lg" loading={submitting} onClick={submit}>
            添加出版社（并选中）
          </Button>
        </div>
      }
    >
      <Input
        label="出版社（必填）"
        value={publisher}
        list={PUBLISHER_DATALIST_ID}
        autoComplete="off"
        onChange={(event) => {
          setPublisher(event.target.value);
          setProblem(null);
        }}
        placeholder="人民教育出版社"
        hint="照书封面或版权页上的那家抄，例如 人民教育出版社、北京师范大学出版社。它和下面选好的「年级 + 学期」一起决定这是哪一册。"
      />
      <datalist id={PUBLISHER_DATALIST_ID}>
        {publisherCandidates.map((candidate) => (
          <option key={candidate} value={candidate} />
        ))}
      </datalist>

      {problem !== null && (
        <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {problem}
        </p>
      )}

      {error !== null && (
        <p className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          没建成：{error}
        </p>
      )}

      <p className="mt-3 text-xs text-slate-500">
        同一个「出版社 + 年级 + 学期」只会有一册：如果已经有了，会自动帮你选中它，不会重复建。
      </p>
    </Modal>
  );
}
